import { ChangeEvent, useCallback, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import LangEnum from '../../../../lang/LangEnum';
import useGeneralSettingsStore from '../../../../stores/GeneralSettings/useGeneralSettingsStore';
import snackbarStore from '../../../../stores/Snackbar.store';
import { useTranslation } from '../../../translation/TranslationContext';

const isValidValue = (valueToCheck: number, MAX?: number) =>
  typeof MAX !== 'number' || valueToCheck <= MAX;

function useMaxValue(initialValue?: string) {
  const { translate } = useTranslation();
  const [value, setValue] = useState(initialValue ?? '');

  const { productInputLockValue } = useGeneralSettingsStore(
    useShallow((state) => ({
      productInputLockValue: state.generalSettings.productInputLockValue,
    })),
  );

  const handleChangeValue = useCallback(
    (event: ChangeEvent<HTMLInputElement>, maxValue?: number, showSnackbar = false) => {
      const inputValue = event.target.value;

      if (inputValue === '') {
        setValue('');
        return;
      }

      const valueToChange = Math.abs(Number(inputValue));

      if (Number.isNaN(valueToChange)) {
        return;
      }

      if (isValidValue(valueToChange, maxValue)) {
        setValue(String(valueToChange));
        return;
      }

      if (typeof maxValue === 'number') {
        const maxDigits = String(maxValue).length;
        const normalizedInputValue = String(valueToChange);
        const candidate = normalizedInputValue.slice(0, maxDigits);
        const newValue =
          Number(candidate) <= maxValue
            ? candidate
            : normalizedInputValue.slice(0, maxDigits - 1) || String(maxValue);
        event.target.value = newValue;
        setValue(newValue);
      } else {
        setValue(inputValue);
      }

      if (!showSnackbar) {
        return;
      }

      if (typeof maxValue === 'number' && productInputLockValue === maxValue) {
        snackbarStore.handleMessage({
          type: 'warning',
          message: translate(LangEnum.PRODUCT_INPUT_UNLOCK_MESSAGE, { max: maxValue }),
        });
      } else {
        snackbarStore.handleMessage({
          type: 'warning',
          message: translate(LangEnum.PRODUCT_INPUT_UNAVAILABLE_MESSAGE),
        });
      }
    },
    [translate, productInputLockValue],
  );

  return { value, handleChangeValue };
}

export default useMaxValue;
